import { useCallback, useState, FC } from 'react';
import {
  Button,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AppointmentCommunicationSummary,
  COMM_KIND_LABELS,
  CommStatus,
} from './types';

interface CommsPanelProps {
  communications: AppointmentCommunicationSummary[];
  onViewConversation: (conversationId: number) => void;
}

const commStatusChip = (status: CommStatus): string => {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'pending':
      return 'Sending';
    case 'failed':
    default:
      return 'Not sent';
  }
};

const CommsPanel: FC<CommsPanelProps> = ({
  communications,
  onViewConversation,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  if (communications.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5} mt={1}>
      <Button
        size="small"
        onClick={handleToggle}
        sx={{ alignSelf: 'flex-start' }}
      >
        {open
          ? 'Hide notifications'
          : `Notifications (${communications.length})`}
      </Button>
      <Collapse in={open}>
        <Table size="small" aria-label="appointment notifications">
          <TableHead>
            <TableRow>
              <TableCell>Notification</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {communications.map((comm) => (
              <TableRow key={comm.id}>
                <TableCell>{COMM_KIND_LABELS[comm.comm_kind]}</TableCell>
                <TableCell>{comm.cert_member_name ?? '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">
                      {commStatusChip(comm.status)}
                    </Typography>
                    {comm.status === 'failed' && comm.error_detail && (
                      <Typography variant="caption" color="text.secondary">
                        ({comm.error_detail})
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {comm.conversation && (
                    <Button
                      size="small"
                      onClick={() =>
                        onViewConversation(comm.conversation as number)
                      }
                    >
                      View message
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Stack>
  );
};

export default CommsPanel;
